import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject, tap, catchError, of, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/auth.model';
import { SessionService } from '../auth/services/session.service';

export interface SystemNotification {
  id: string;
  usuarioId: string;
  tipo: string;
  titulo: string;
  cuerpo: string;
  dataJson?: any;
  leido: boolean;
  fechaCreacion: string;
  fechaLeido?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SystemNotificationService {
  private readonly baseUrl = `${environment.apiUrl}/notificaciones`;
  private http = inject(HttpClient);
  private sessionService = inject(SessionService);
  
  // Signals para el estado de notificaciones
  private notificationsState = signal<{
    notifications: SystemNotification[];
    unreadCount: number;
    loading: boolean;
    error: string | null;
  }>({
    notifications: [],
    unreadCount: 0,
    loading: false,
    error: null
  });

  // Computed signals
  public readonly notifications = computed(() => this.notificationsState().notifications);
  public readonly unreadCount = computed(() => this.notificationsState().unreadCount);
  public readonly loading = computed(() => this.notificationsState().loading);
  public readonly error = computed(() => this.notificationsState().error);

  // Subject para notificar cambios
  private notificationsUpdated$ = new BehaviorSubject<void>(undefined);

  // Subject para emitir eventos de push por tipo (ej: SOLICITUD_CREADA, RESERVA_CREADA, etc.)
  private pushEvent$ = new Subject<{ tipo: string; payload: any }>();
  
  // Flag para evitar cargas duplicadas
  private lastAuthState: boolean | null = null;

  constructor() {
    // Suscribirse a cambios de sesión para cargar/limpiar notificaciones
    this.sessionService.sessionState$.subscribe(session => {
      // Solo actuar si el estado de autenticación cambió realmente
      if (session.isAuthenticated === this.lastAuthState) {
        return;
      }
      this.lastAuthState = session.isAuthenticated;
      
      if (session.isAuthenticated) {
        // Solo cargar si el usuario está autenticado
        this.loadNotifications();
        this.loadUnreadCount();
      } else {
        // Limpiar notificaciones si el usuario se desautentica
        this.updateState({
          notifications: [],
          unreadCount: 0,
          loading: false,
          error: null
        });
      }
    });
  }

  /**
   * Carga todas las notificaciones del usuario
   */
  loadNotifications(): void {
    // Verificar que el usuario esté autenticado
    if (!this.sessionService.isLoggedIn()) {
      this.updateState({
        notifications: [],
        unreadCount: 0,
        loading: false,
        error: null
      });
      return;
    }

    this.updateState({ loading: true, error: null });

    this.http.get<ApiResponse<SystemNotification[]>>(`${this.baseUrl}`)
      .pipe(
        tap(response => {
          if (response.exito && response.data) {
            this.updateState({
              notifications: response.data,
              loading: false
            });
            this.notificationsUpdated$.next();
          } else {
            this.updateState({
              error: response.mensaje || 'Error al cargar notificaciones',
              loading: false
            });
          }
        }),
        catchError(error => {
          console.error('Error al cargar notificaciones:', error);
          this.updateState({
            error: 'Error al cargar notificaciones',
            loading: false
          });
          return of(null);
        })
      )
      .subscribe();
  }

  /**
   * Carga solo las notificaciones no leídas
   */
  loadUnreadNotifications(): Observable<SystemNotification[]> {
    this.updateState({ loading: true, error: null });

    return this.http.get<ApiResponse<SystemNotification[]>>(`${this.baseUrl}/no-leidas`)
      .pipe(
        map(response => {
          if (response.exito && response.data) {
            this.updateState({
              notifications: response.data,
              loading: false
            });
            this.notificationsUpdated$.next();
            return response.data;
          } else {
            this.updateState({
              error: response.mensaje || 'Error al cargar notificaciones no leídas',
              loading: false
            });
            return [];
          }
        }),
        catchError(error => {
          console.error('Error al cargar notificaciones no leídas:', error);
          this.updateState({
            error: 'Error al cargar notificaciones no leídas',
            loading: false
          });
          return of([]);
        })
      );
  }

  /**
   * Carga el conteo de notificaciones no leídas
   */
  loadUnreadCount(): void {
    // Verificar que el usuario esté autenticado
    if (!this.sessionService.isLoggedIn()) {
      this.updateState({
        unreadCount: 0
      });
      return;
    }

    this.http.get<ApiResponse<number>>(`${this.baseUrl}/no-leidas/count`)
      .pipe(
        tap(response => {
          if (response.exito && response.data !== undefined) {
            this.updateState({
              unreadCount: response.data
            });
          }
        }),
        catchError(error => {
          console.error('Error al cargar conteo de notificaciones no leídas:', error);
          return of(null);
        })
      )
      .subscribe();
  }

  /**
   * Marca una notificación como leída
   */
  markAsRead(notificacionId: string): Observable<SystemNotification> {
    return this.http.put<ApiResponse<SystemNotification>>(`${this.baseUrl}/${notificacionId}/marcar-leida`, {})
      .pipe(
        tap(response => {
          if (response.exito && response.data) {
            // Actualizar la notificación en el estado
            const currentNotifications = this.notificationsState().notifications;
            const updatedNotifications = currentNotifications.map(n =>
              n.id === notificacionId ? response.data : n
            );
            
            // Recalcular el conteo no leído
            const newUnreadCount = updatedNotifications.filter(n => !n.leido).length;
            
            this.updateState({
              notifications: updatedNotifications,
              unreadCount: newUnreadCount
            });
            this.notificationsUpdated$.next();
          }
        }),
        catchError(error => {
          console.error('Error al marcar notificación como leída:', error);
          return of(null as any);
        }),
        map(response => response?.exito && response.data ? response.data : null as any)
      );
  }

  /**
   * Marca todas las notificaciones como leídas
   */
  markAllAsRead(): Observable<void> {
    return this.http.put<ApiResponse<void>>(`${this.baseUrl}/marcar-todas-leidas`, {})
      .pipe(
        tap(response => {
          if (response.exito) {
            // Actualizar todas las notificaciones como leídas
            const currentNotifications = this.notificationsState().notifications;
            const updatedNotifications = currentNotifications.map(n => ({
              ...n,
              leido: true,
              fechaLeido: new Date().toISOString()
            }));
            
            this.updateState({
              notifications: updatedNotifications,
              unreadCount: 0
            });
            this.notificationsUpdated$.next();
          }
        }),
        catchError(error => {
          console.error('Error al marcar todas las notificaciones como leídas:', error);
          return of(undefined);
        }),
        map(() => undefined)
      );
  }

  /**
   * Obtiene una notificación por ID
   */
  getNotificationById(notificacionId: string): Observable<SystemNotification | null> {
    return this.http.get<ApiResponse<SystemNotification>>(`${this.baseUrl}/${notificacionId}`)
      .pipe(
        map(response => response.exito && response.data ? response.data : null),
        catchError(error => {
          console.error('Error al obtener notificación:', error);
          return of(null);
        })
      );
  }

  /**
   * Observable para escuchar actualizaciones de notificaciones
   */
  get notificationsUpdated(): Observable<void> {
    return this.notificationsUpdated$.asObservable();
  }

  /**
   * Observable para escuchar eventos push por tipo de notificación.
   * Los componentes pueden filtrar por tipo: pushEvents.pipe(filter(e => e.tipo === 'SOLICITUD_CREADA'))
   */
  get pushEvents(): Observable<{ tipo: string; payload: any }> {
    return this.pushEvent$.asObservable();
  }

  /**
   * Emite un evento push recibido para que los componentes suscritos reaccionen.
   */
  emitPushEvent(tipo: string, payload: any): void {
    this.pushEvent$.next({ tipo, payload });
  }

  /**
   * Actualiza el estado interno
   */
  private updateState(partial: Partial<typeof this.notificationsState extends () => infer T ? T : never>): void {
    this.notificationsState.update(state => ({ ...state, ...partial }));
  }
}

